<template>
  <div>
    <v-dialog v-model="dialog" persistent scrollable max-width="720px">
      <v-card v-if="Student" v-draggable-dialog class="profile-dialog-card student-edit-dialog">
        <div class="dialog-drag-handle profile-dialog-header student-edit-dialog__header">
          <div class="student-edit-dialog__title">
            <v-avatar size="44" color="green lighten-5">
              <v-icon color="primary">mdi-account-edit-outline</v-icon>
            </v-avatar>
            <div>
              <span>Edit student</span>
              <h2>{{ studentDisplayName }}</h2>
              <p>Update personal details.</p>
            </div>
          </div>
          <div class="student-edit-dialog__header-actions">
            <v-icon small color="grey lighten-1" aria-hidden="true">mdi-drag</v-icon>
            <v-btn icon aria-label="Close edit student" @click.stop="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider />

        <v-card-text class="profile-dialog-body student-edit-dialog__body">
          <section class="student-edit-section">
            <div class="student-edit-section__heading">
              <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
              <div>
                <h3>Student information</h3>
                <p>Basic details used throughout Taleem.</p>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.trim="Student.firstName"
                  outlined
                  :hide-details="nameErrors.length === 0"
                  dense
                  autofocus
                  :error-messages="nameErrors"
                  label="First name"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.lastName" outlined dense hide-details label="Last name" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="Student.gender"
                  :items="genderOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  label="Gender"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Student.dob"
                  outlined
                  dense
                  type="date"
                  :max="maxDob"
                  label="Date of birth"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.phoneNumber" outlined dense hide-details label="Phone number" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.email" outlined dense hide-details label="Email address" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model.trim="Student.reference" outlined dense hide-details label="Reference" />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="Student.description"
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  hide-details
                  label="Description"
                />
              </v-col>
            </v-row>
          </section>
        </v-card-text>

        <v-divider />
        <v-card-actions class="profile-dialog-actions student-edit-dialog__actions">
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="loading" @click="submit">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import studentService from "@/modules/Student/service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

function emptyStudent() {
  return {
    firstName: null,
    lastName: null,
    email: null,
    gender: "male",
    dob: null,
    phoneNumber: null,
    reference: null,
    description: null,
  };
}

export default {
  name: "update-Student",
  mixins: [validationMixin],
  props: {
    editDialog: {
      default: false,
    },
    editStudent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      Student: emptyStudent(),
      dialog: false,
      loading: false,
      genderOptions: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" },
      ],
    };
  },
  validations: {
    Student: {
      firstName: { required },
    },
  },
  computed: {
    maxDob() {
      return this.$moment().format("YYYY-MM-DD");
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required) errors.push("First name is required");
      return errors;
    },
    studentDisplayName() {
      const fullName = [this.Student.firstName, this.Student.lastName].filter(Boolean).join(" ").trim();
      return fullName || "Student details";
    },
  },
  watch: {
    editDialog(val) {
      this.dialog = val;
      if (!val || !this.editStudent) return;

      this.Student = {
        id: this.editStudent.id,
        firstName: this.editStudent.firstName,
        lastName: this.editStudent.lastName,
        gender: this.editStudent.gender,
        dob: this.editStudent.dob ? this.$moment(this.editStudent.dob).format("YYYY-MM-DD") : null,
        phoneNumber: this.editStudent.phoneNumber,
        email: this.editStudent.email,
        reference: this.editStudent.reference,
        description: this.editStudent.description,
      };
    },
  },
  methods: {
    close() {
      this.dialog = false;
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Student = emptyStudent();
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        }).fire({
          icon: "error",
          title: this.$t("toastMessage.text.invalidFields"),
        });
        return;
      }

      this.loading = true;
      return studentService.update(this.Student.id, this.Student)
        .then((result) => {
          if (result.status) {
            this.$swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            }).fire({ icon: "success", title: "Student is updated" });
            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          }).fire({
            icon: "error",
            title: err && err.data ? err.data.message : "Student could not be updated",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.student-edit-dialog__header {
  background: linear-gradient(135deg, #f7fbf8 0%, #edf7ef 100%);
}

.student-edit-dialog__title,
.student-edit-dialog__header-actions,
.student-edit-section__heading {
  display: flex;
  align-items: center;
}

.student-edit-dialog__title {
  min-width: 0;
  gap: 12px;
}

.student-edit-dialog__title > div {
  min-width: 0;
}

.student-edit-dialog__title span {
  color: #2f7d4a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.student-edit-dialog__title h2 {
  overflow: hidden;
  margin: 1px 0 0;
  color: #253047;
  font-size: 18px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-edit-dialog__title p {
  margin: 2px 0 0;
  color: #7f8c82;
  font-size: 10px;
}

.student-edit-dialog__header-actions {
  flex: 0 0 auto;
  gap: 5px;
}

.student-edit-dialog__body {
  background: #f8faf8;
}

.student-edit-section {
  margin-bottom: 14px;
  padding: 17px;
  border: 1px solid #e2e9e3;
  border-radius: 12px;
  background: #fff;
}

.student-edit-section:last-child {
  margin-bottom: 0;
}

.student-edit-section__heading {
  gap: 10px;
  margin-bottom: 15px;
}

.student-edit-section__heading > span:first-child {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf7ef;
}

.student-edit-section__heading > div {
  min-width: 0;
  flex: 1;
}

.student-edit-section__heading h3 {
  margin: 0;
  color: #293448;
  font-size: 14px;
}

.student-edit-section__heading p {
  margin: 2px 0 0;
  color: #8a958d;
  font-size: 10px;
}

.student-edit-section ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 9px;
}

.student-edit-dialog__actions {
  background: #fff;
}

@media (max-width: 599px) {
  .student-edit-dialog__header {
    min-height: 70px;
    padding: 13px 14px;
  }

  .student-edit-dialog__title p,
  .student-edit-dialog__header-actions > .v-icon {
    display: none;
  }

  .student-edit-dialog__body {
    padding: 14px !important;
  }

  .student-edit-section {
    padding: 14px;
  }
}
</style>
